'use client';

import { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { portalThemeColors, portalThemeDefaultConfig } from '@nx-frontend/shared/themes';
import {
  PortalColorSchemas,
  ThemeColorSchemas,
  PortalThemeKeys,
  WebnotesThemeKeys,
  PortalThemeTemplate,
  PortalThemeColors,
} from '@nx-frontend/shared/types';
import { OnThemeSelect, ThemeItem } from './theme-item';

interface ThemeListProps extends OnThemeSelect {
  selectedKey: PortalThemeKeys | WebnotesThemeKeys;
  showHiddenThemes?: boolean;
  selectComingSoonThemes?: boolean;
  onDetailedSettings?: () => void;
  hideDescription?: boolean;
  itemClass?: string;
  showSelected?: boolean;
  wizardMode?: boolean;
  shuffleThemes?: boolean;
  themeImgPrefix?: string;
  mobileNav?: boolean;
  showStatus?: boolean;
  showEdit?: boolean;
}

/**
 *  previewUrl by templateKey
 * @param portalThemes - themes array
 * @param templateKey - themes key
 * @returns URL
 */
export const getPreviewUrlByTemplateKey = (
  portalThemes: PortalThemeTemplate[],
  templateKey: string
): string | undefined => {
  const theme = portalThemes.find((theme) => theme.templateKey === templateKey);
  return theme ? theme.previewUrl : undefined;
};

export const ThemesList: React.FC<ThemeListProps> = ({
  selectedKey,
  onSelect,
  showHiddenThemes = false,
  selectComingSoonThemes = false,
  onDetailedSettings,
  hideDescription,
  itemClass,
  showSelected,
  wizardMode,
  shuffleThemes,
  themeImgPrefix,
  mobileNav,
  showStatus,
  showEdit,
}) => {
  const { style: styleConfig } = portalThemeDefaultConfig;

  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const themesContainerRef = useRef<HTMLDivElement | null>(null);

  const themes = useMemo(() => {
    let themes: PortalThemeColors[] = [];
    if (shuffleThemes) {
      themes = portalThemeColors.sort((a, b) => {
        if (a.key === selectedKey) return -1;
        if (b.key === selectedKey) return 1;
        return Math.random() - 0.5;
      });
    } else {
      themes = portalThemeColors.sort((a, b) => {
        if (a.key === selectedKey) return -1;
        if (b.key === selectedKey) return 1;
        return 0.9;
      });
    }
    return themes.filter((theme) => {
      return !(!showHiddenThemes && theme.hidden);
    });
    // eslint-disable-next-line
  }, [shuffleThemes]);

  const goTo = (direction: 'prev' | 'next') => {
    if (themesContainerRef.current) {
      const currentIndex = themes.findIndex((item) => item.key === selectedKey);

      if (currentIndex !== -1) {
        let newIndex;
        if (direction === 'next') {
          if (currentIndex === themes.length - 1) return;
          newIndex = currentIndex + 1;
        } else {
          if (currentIndex === 0) return;
          newIndex = currentIndex - 1;
        }

        const nextItem = themes[newIndex];
        if (nextItem) {
          onSelect(nextItem.key);

          const nextElement = document.querySelector(`[data-key="${nextItem.key}"]`);
          if (nextElement && themesContainerRef.current) {
            const container = themesContainerRef.current;
            if ('offsetLeft' in nextElement && typeof nextElement.offsetLeft === 'number') {
              const scrollPosition = nextElement.offsetLeft - container.offsetLeft;
              container.scrollTo({
                left: scrollPosition,
                behavior: 'smooth',
              });
            }
          }

          if (newIndex === 0) {
            setIsPrevDisabled(true);
          } else if (newIndex === themes.length - 1) {
            setIsNextDisabled(true);
          } else {
            setIsPrevDisabled(false);
            setIsNextDisabled(false);
          }
        }
      }
    }
  };

  return (
    <>
      {mobileNav && (
        <div className="flex mb-5 gap-3 lg:hidden">
          <div
            className={classNames('w-11 h-11 border border-neutral-200 flex items-center justify-center rounded-md', {
              'opacity-30': isPrevDisabled,
            })}
            onClick={() => goTo('prev')}
          >
            <Icon icon={IconsMap.ARROW_BACKWARD} className="w-4 h-4 text-neutral-800" />
          </div>
          <div
            className={classNames('w-11 h-11 border border-neutral-200 flex items-center justify-center rounded-md', {
              'opacity-30': isNextDisabled,
            })}
            onClick={() => goTo('next')}
          >
            <Icon icon={IconsMap.ARROW_FORWARD} className="w-4 h-4 text-neutral-800" />
          </div>
        </div>
      )}

      <div className="flex gap-4 lg:flex-wrap overflow-auto pb-2 max-h-full" ref={themesContainerRef}>
        {themes.map((colors) => {
          if (colors.hidden && !showHiddenThemes) {
            return null;
          }
          const themeColors: ThemeColorSchemas = [
            {
              schema: PortalColorSchemas.LIGHT,
              colors,
            },
          ];
          const thumbnail = wizardMode ? colors.thumbnail.replace('/assets', themeImgPrefix ?? '') : colors.thumbnail;
          return (
            <ThemeItem
              key={colors.key}
              onDetailedSettings={onDetailedSettings}
              showStatus={showStatus}
              showEdit={showEdit}
              themeKey={colors.key}
              themeName={colors.name}
              thumbnail={thumbnail}
              description={!hideDescription ? colors.description : undefined}
              themeColors={themeColors}
              styleConfig={styleConfig}
              comingSoon={colors.comingSoon}
              selectComingSoonThemes={selectComingSoonThemes}
              selected={selectedKey === colors.key}
              itemClass={itemClass}
              showSelected={showSelected}
              onSelect={onSelect}
            />
          );
        })}
      </div>
    </>
  );
};
